import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { IoSearch } from 'react-icons/io5';
import AddContactForm from './AddContactForm';
import CountryFlag from '../FlagComponent/CountryFlag';
import { renderMessageContent } from './LastMessageUtils';
import ClipLoader from 'react-spinners/ClipLoader';

import '../../Styles/ChatsList.css';
import '../../Styles/Header.css';
import { Link } from 'react-router-dom';
import { FaCheck } from "react-icons/fa6";
import { BiCheckDouble } from "react-icons/bi";
import { API_URL } from '../../config';
import { io } from "socket.io-client";
import debounce from "lodash.debounce";

const fetchChats = async (controller,{ listSize, searchTerm, filterOption }) => {
  const token = localStorage.getItem('token');
  const response = await axios.get(`${API_URL}getWhatsappNames`, {
    headers: {
      'Authorization': `Bearer ${token}`
    },
    params: {
      amount: listSize + 20,
      search: searchTerm,
      admin: filterOption,
    },
    signal: controller.signal,
  });

  return response.data.data;
};

const ChatsList = ({isActive}) => {
  const [chats, setChats] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOption, setFilterOption] = useState('');
  const [isActiveState, setIsActiveState] = useState(isActive);
  const ref = useRef();
  const chatsListRef = useRef(null);
  const controllerRef = useRef(null);
  const requestSequenceRef = useRef(0);
  const [listSize, setListSize] = useState(10);
  const searchTermRef = useRef('');
  const filterOptionRef = useRef('');
  const listSizeRef = useRef(10);
  const [loading, setLoading] = useState(false);
  const [pageLoad, setPageload] = useState(false);
  const [activePhone, setactivePhone] = useState(0);

  const triggerFetchChats = useMemo(
  () => debounce(async () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }

    const controller = new AbortController();
    const requestSequence = ++requestSequenceRef.current;
    controllerRef.current = controller;

    const timeoutId = setTimeout(() => {
      controller.abort();
    }, 10000);

    try {
      const nextChats = await fetchChats(controller, {
        listSize: listSizeRef.current,
        searchTerm: searchTermRef.current,
        filterOption: filterOptionRef.current,
      });

      if (requestSequence === requestSequenceRef.current) {
        setChats(nextChats);
      }
    } catch (error) {
      if (!axios.isCancel(error) && error.name !== "AbortError") {
        console.error("Error fetching chats:", error);
      }
    } finally {
      clearTimeout(timeoutId);
      if (requestSequence === requestSequenceRef.current) {
        setLoading(false);
        setPageload(false);
      }
    }
  }, 300),
  []
  );

  useEffect(() => {
    return () => {
      triggerFetchChats.cancel();
      controllerRef.current?.abort();
    };
  }, [triggerFetchChats]);

  useEffect(() => {
    setPageload(true);
    requestSequenceRef.current += 1;
    controllerRef.current?.abort();
    triggerFetchChats();
  }, [listSize, triggerFetchChats]);

  useEffect(() => {
    setLoading(true);
    listSizeRef.current = 10;
    setListSize(10);
    chatsListRef.current?.scrollTo({ top: 0 });
    requestSequenceRef.current += 1;
    controllerRef.current?.abort();
    triggerFetchChats();
  }, [filterOption, triggerFetchChats]);

  useEffect(() => {
    setLoading(true);
    setListSize(10);
    listSizeRef.current = 10;
    chatsListRef.current?.scrollTo({ top: 0 });
    requestSequenceRef.current += 1;
    controllerRef.current?.abort();
    triggerFetchChats();
  }, [searchTerm, triggerFetchChats]);

  useEffect(() => {
    const socket = io(process.env.REACT_APP_SOCKETIO, {
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      timeout: 10000,
    });

    const refreshChats = () => {
      requestSequenceRef.current += 1;
      controllerRef.current?.abort();
      triggerFetchChats();
    };

    socket.on("connect", refreshChats);
    socket.io.on("reconnect", refreshChats);
    socket.on("newMessage", (data) => {
      if (!data?.type || data.type === "chat_list_refresh") {
        refreshChats();
        return;
      }

      if (data.type !== "new_message" && data.type !== "status_update") {
        return;
      }

      const messageData = data.data;

      if (!messageData?.phone) {
        refreshChats();
        return;
      }

      setChats(prevChats => {
        const existingChatIndex = prevChats.findIndex(
          chat => String(chat.phone) === String(messageData.phone)
        );

        if (existingChatIndex === -1) {
          return prevChats;
        }

        const updatedChats = [...prevChats];
        updatedChats[existingChatIndex] = {
          ...updatedChats[existingChatIndex],
          message: messageData.message,
          timestamp: messageData.timestamp,
          status: messageData.status,
          is_new: messageData.is_new,
          sent: messageData.sent
        };

        return updatedChats.sort(
          (left, right) =>
            Number(right.timestamp || 0) - Number(left.timestamp || 0) ||
            Number(right.id || 0) - Number(left.id || 0)
        );
      });

      if (data.type === "new_message") {
        refreshChats();
      }
    });

    return () => {
      socket.off("connect", refreshChats);
      socket.off("newMessage");
      socket.io.off("reconnect", refreshChats);
      socket.disconnect();
    };
  }, [triggerFetchChats]);

  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}getAdmins`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data.status === 'success') {
          const unblockedAdmins = response.data.data.filter(admin => !admin.blocked);
          setAdmins(unblockedAdmins);
        }
      } catch (error) {
        console.error('Error fetching admins:', error);
      }
    };

    fetchAdmins();
  }, []);

  const handleSearchChange = (event) => {
    searchTermRef.current = event.target.value;
    setSearchTerm(event.target.value);
  };

  const readMessage = async (id) => {
    setChats((prevChats) =>
      prevChats.map((chat) =>
        chat.id === id ? { ...chat, is_new: '0' } : chat
      )
    );
  
    try {
      const response = await axios.post(`${API_URL}readMessage`, { id }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
  
      if (response.data.status !== 'success') {
        throw new Error("Failed to update read status");
      }
  
    } catch (error) {
      console.error("Error marking message as read:", error.response?.data || error.message);
      
      setChats((prevChats) =>
        prevChats.map((chat) =>
          chat.id === id ? { ...chat, is_new: '1' } : chat
        )
      );
    }
  };

  useEffect(()=> {
    if (!ref.current) return;

    if(parseInt(searchTerm[0]) || searchTerm[0] === "+") {
      ref.current.dir = 'ltr';
    }
    else {
      ref.current.dir = 'rtl';
    }

  },[searchTerm])

  useEffect(() => {
    const phone = window.location.pathname.split('/')[2];
    if (chats.length > 0 && phone) {
      const foundChat = chats.find(chat => chat.phone === phone);
      if (foundChat && foundChat.is_new === 1) {
        readMessage(foundChat.id);
      }
    }
  }, [chats]);

  const handleFilterChange = event => {
    filterOptionRef.current = event.target.value;
    setFilterOption(event.target.value);
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    if (now.toDateString() === date.toDateString()) {
      return `${hours}:${minutes}`;
    } else {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      return `${hours}:${minutes}, ${day}/${month}`;
    }
  };

  const listPage = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const bottomScroll = scrollHeight - clientHeight;
    if(scrollTop > (bottomScroll - 100) && listSize < chats.length) {
      listSizeRef.current = listSize + 10;
      setListSize(listSize + 10);
    }
  };

  return (
    <div className={`chats-container ${!isActiveState ? 'hide-mobile' : ''}`}>
      <div className="chats-header-container">
        <div className="title">المحادثات</div>
        <AddContactForm />
      </div>

      <div className="chats-search-container">
        <input
          type="text"
          placeholder="بحث"
          ref={ref}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <IoSearch className="search-icon" />
      </div>

      <div className="chats-filter-container">
        <p>فلتر حسب المسؤول</p>
        <select className="filter-option" value={filterOption} onChange={handleFilterChange}>
          <option value="">الكل</option>
          {admins.map((admin) => (
            <option key={admin.id} value={admin.id}>{admin.name}</option>
          ))}
        </select>
      </div>

      <div className={`chats-list-container ${loading || chats.length === 0 ? 'is-empty' : ''}`}>
        {loading ? (
          <ClipLoader />
        ) : chats.length === 0 ? (
          <div className="no-results-message">لا يوجد نتائج</div>
        ) : (
          <div className="chats" ref={chatsListRef} onScroll={listPage}>
            {chats.map((chat) => (
              <Link key={chat.phone} to={`/chat/${chat.phone}`} className="chat-link" onClick={()=>{
              setIsActiveState(false);
              setactivePhone(chat.phone);
              if(chat.is_new === 1){
                readMessage(chat.id);
              }
              }} >
                <div className={`chat-item ${String(chat.phone) === String(activePhone) || String(chat.phone) === window.location.pathname.split('/')[2] ? 'chat-item-selected' : ''} ${chat.is_new && String(chat.phone) !== String(activePhone) && String(chat.phone) !== window.location.pathname.split('/')[2] ? 'is-new' : ''}`}>
                  <div className="flag" key={chat.id}>
                    <CountryFlag phoneNumber={`+${chat.phone}`} />
                  </div>
                  <div className="chat-info">
                    <div className="chat-list-title">{chat.nickname || chat.realname || chat.previousName || chat.phone} ({chat.adminName})</div>
                    <div className={`last-message ${chat.is_new ? 'is-new' : ''}`}>
                      {chat.sent
                        ? chat.status === 'sent'
                          ? <FaCheck className='sent-status' />
                          : chat.status === 'delivered'
                            ? <BiCheckDouble className='delivered-status' />
                            : chat.status === 'read'
                              ? <BiCheckDouble className='read-status' />
                              : ''
                        : ''} &nbsp;
                        {renderMessageContent(chat.message)}
                    </div>
                  </div>
                  <div className="last-message-info">
                    <div className="last-message-date">{formatDate(chat.timestamp)}</div>
                    {chat.is_new === 1 && String(chat.phone) !== String(activePhone) && String(chat.phone) !== window.location.pathname.split('/')[2] ? <div className="new-message-marker"></div> : ''}
                  </div>
                </div>
              </Link>
            ))}
            {pageLoad &&
            <div className='loading-container'>
              <ClipLoader /> 
            </div>
            }
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatsList;
